// =====================================================================================
// INVRT Radar — web console — one site — Consent tab
//
// How a site captures consent (docs/API-CONTRACTS-C1.md §4). Two modes, chosen here and
// saved with POST /sites/:id/consent:
//
//   radar_banner  Radar's own banner, designed below (./consent/BannerStudio), published
//                 whole with PUT /sites/:id/banner, served to visitors by the loader
//                 (web/public/r.js). Choice counts come from GET /sites/:id/consent/stats.
//   existing_cmp  The tenant's own consent platform governs the pixel; the install snippet
//                 is unchanged and nothing here applies beyond the mode itself.
//
// Administrators change things; every other member sees the same panel read-only. The page
// shell registers this under the tab id "consent".
// =====================================================================================

import { useState } from "react";
import { useSession } from "@/auth/SessionProvider";
import { ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import type { ConsentMode, Site } from "@/lib/types";
import type { ConsentSettings } from "@/lib/types.c1";
import { BannerStudio } from "./consent/BannerStudio";
import { ConsentStatsCard } from "./consent/ConsentStatsCard";
import { useConsentSettings, useSaveConsentMode } from "./consent/useConsent";

const MODE_LABELS: Record<ConsentMode, string> = {
  radar_banner: "Radar banner",
  existing_cmp: "Your own consent platform",
};

function ModeCard({
  siteId,
  settings,
  isAdmin,
}: {
  siteId: string;
  settings: ConsentSettings;
  isAdmin: boolean;
}) {
  const { push } = useToast();
  const save = useSaveConsentMode(siteId);
  const [choice, setChoice] = useState<ConsentMode | null>(null);
  const selected = choice ?? settings.mode;
  const changed = selected !== settings.mode;

  function handleSave(): void {
    if (!changed) return;
    save.mutate(selected, {
      onSuccess: () => {
        setChoice(null);
        push({ tone: "success", title: "Consent mode saved" });
      },
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Consent</CardTitle>
        <CardDescription>
          Every change is kept as a dated record, so you can always show how consent was
          captured on a given day. Radar provides the mechanism; confirm it meets your own
          obligations with whoever signs off compliance.
        </CardDescription>
        {!isAdmin ? (
          <p className="text-sm text-ink-muted">
            Administrators manage consent. You can see the current banner and its figures;
            only an administrator can change or publish it.
          </p>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select
            label="How is consent captured?"
            value={selected}
            disabled={!isAdmin || save.isPending}
            onChange={(event) => setChoice(event.target.value as ConsentMode)}
            className="w-72 max-w-full"
          >
            {(Object.keys(MODE_LABELS) as ConsentMode[]).map((mode) => (
              <option key={mode} value={mode}>
                {MODE_LABELS[mode]}
              </option>
            ))}
          </Select>
          {isAdmin ? (
            <Button type="button" onClick={handleSave} disabled={!changed} loading={save.isPending}>
              Save mode
            </Button>
          ) : null}
        </div>

        {changed && selected === "radar_banner" ? (
          <p className="text-sm text-ink-muted">
            Saving switches this site to Radar&apos;s banner. Design and publish it below to
            make it live.
          </p>
        ) : null}
        {changed && selected === "existing_cmp" ? (
          <p className="text-sm text-ink-muted">
            Saving hands consent to your own platform. Any Radar banner you published stops
            being served, and the install snippet goes back to the pixel.
          </p>
        ) : null}

        {save.isError ? <ErrorState error={save.error} /> : null}
      </CardContent>
    </Card>
  );
}

export function ConsentPanel({ site }: { site: Site }) {
  const { activeMembership } = useSession();
  const isAdmin = activeMembership?.is_org_admin === true;
  const settings = useConsentSettings(site.id);

  if (settings.isLoading) return <LoadingState label="Loading consent settings…" />;
  if (settings.isError) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  }
  if (!settings.data) return null;

  return (
    <div className="flex flex-col gap-4">
      <ModeCard siteId={site.id} settings={settings.data} isAdmin={isAdmin} />

      {settings.data.mode === "radar_banner" ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Banner</CardTitle>
              <CardDescription>
                Design the banner your visitors see. The preview is the real thing: the same
                script, the same copy, the same colours.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <BannerStudio siteId={site.id} settings={settings.data} isAdmin={isAdmin} />
            </CardContent>
          </Card>
          <ConsentStatsCard siteId={site.id} />
        </>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Your consent platform</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm text-ink-muted">
            <p>
              Your own consent platform governs the pixel on this site. The install snippet is
              unchanged: keep it in the category your platform blocks until consent is given,
              and wrap it in your platform&apos;s consent-granted callback before publishing.
            </p>
            <p>Switch to the Radar banner above if you would rather Radar ask for consent.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
